import styled from '@emotion/styled';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Field, Formik, useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { warrantService } from '../../../api/services/warrant.service';
import { buyerSeatService } from '../../../api/services/buyer-seat.service';
import { applicationService } from '../../../api/services/application.service';
import { OrganizationDetailDto, OrganizationUpdateDto, BuyerSeatOverviewItemDto, BuyerSeatPageDto } from '../../../api/services/dto/user-managment-dto/organization-detail-dto';
import { OwnerDto } from '../../../api/services/dto/user-managment-dto/owner-dto';
import { ORG_STATUS } from '../../../api/services/dto/user-managment-dto/organization-list-dto';
import { DealOverviewDto } from '../../../api/services/dto/deal-overview-dto';
import { DEAL_STATUS } from '../../../util/deals-util';
import { useAlert } from '../../shared/AlertProvider';
import OrganizationBreadcrumb from '../OrganizationBreadcrumb';
import { getColor } from '../../../theme/colors/colors';
import SelectOwner from './cards/SelectOwner';

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }: TablePaginationActionsProps) => (
  <Box sx={{ flexShrink: 0, ml: 2.5 }}>
    <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
      <KeyboardArrowLeft />
    </IconButton>
    <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
      <KeyboardArrowRight />
    </IconButton>
  </Box>
);

const STATUS_LABELS: Record<string, string> = {
  [ORG_STATUS.ACTIVE]: 'Active',
  [ORG_STATUS.INACTIVE]: 'Inactive',
  [ORG_STATUS.DRAFT]: 'Draft',
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
};

const emptyOwner: OwnerDto = { userId: '', fullName: '', email: '' };

const FormikOwnerWatcher = ({ onOwnerChange }: { onOwnerChange: (owner: OwnerDto) => void }) => {
  const { values } = useFormikContext<{ owner: OwnerDto }>();
  useEffect(() => {
    onOwnerChange(values.owner);
  }, [values.owner, onOwnerChange]);
  return null;
};

const OwnerSelectorField = ({
  initialOwner,
  onOwnerChange,
  onDirty,
}: {
  initialOwner: OwnerDto;
  onOwnerChange: (owner: OwnerDto) => void;
  onDirty: () => void;
}) => (
  <Formik initialValues={{ owner: initialOwner }} onSubmit={() => {}} enableReinitialize>
    {() => (
      <>
        <FormikOwnerWatcher onOwnerChange={onOwnerChange} />
        <Field name="owner">
          {({ field, meta, form }: any) => (
            <SelectOwner
              selected={field.value || emptyOwner}
              name="owner"
              id="owner"
              setCustomDirtyState={() => {
                form.setFieldTouched('owner', true);
                onDirty();
              }}
              helperText={meta.touched && !!meta.error}
            />
          )}
        </Field>
      </>
    )}
  </Formik>
);

const OrganizationDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { setAlert } = useAlert();

  const [org, setOrg] = useState<OrganizationDetailDto | null>(null);
  const [editValues, setEditValues] = useState<OrganizationUpdateDto>({});
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const selectedOwnerRef = useRef<OwnerDto>(emptyOwner);

  const handleOwnerChange = useCallback((owner: OwnerDto) => {
    selectedOwnerRef.current = owner;
  }, []);

  // Buyer Seats table state
  const [buyerSeats, setBuyerSeats] = useState<BuyerSeatOverviewItemDto[]>([]);
  const [buyerSeatTotal, setBuyerSeatTotal] = useState(0);
  const [bsPage, setBsPage] = useState(0);
  const [bsRowsPerPage, setBsRowsPerPage] = useState(10);
  const [bsSearch, setBsSearch] = useState('');
  const [bsSearchDebounced, setBsSearchDebounced] = useState('');
  const [bsStatus, setBsStatus] = useState<string>('All');

  // Deals table state
  const [deals, setDeals] = useState<DealOverviewDto[]>([]);
  const [dealsTotal, setDealsTotal] = useState(0);
  const [dPage, setDPage] = useState(0);
  const [dRowsPerPage, setDRowsPerPage] = useState(10);
  const [dSearch, setDSearch] = useState('');
  const [dSearchDebounced, setDSearchDebounced] = useState('');
  const [dStatus, setDStatus] = useState<string>('ALL');

  // Load org details
  useEffect(() => {
    if (!id) return;
    warrantService
      .getOrganizationById(id)
      .then(setOrg)
      .catch((reason) =>
        setAlert({ open: true, severity: 'error', title: 'Failed to load organization', message: reason.message })
      );
  }, [id]);

  // Click-outside handler to exit edit mode (works with MUI portals)
  useEffect(() => {
    if (!isEditing) return;
    const handleMouseDown = (e: Event) => {
      const target = e.target as Element;
      const isInCard = cardRef.current?.contains(target);
      const isInActions = actionsRef.current?.contains(target);
      // MUI portals (Select dropdown, etc.) render outside the card DOM tree
      const isInPortal =
        !!target.closest('[role="listbox"]') ||
        !!target.closest('[role="presentation"]') ||
        !!target.closest('.MuiPopover-root') ||
        !!target.closest('.MuiModal-root');
      if (!isInCard && !isInActions && !isInPortal) {
        setIsEditing(false);
        setEditValues({});
        setIsDirty(false);
        selectedOwnerRef.current = emptyOwner;
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isEditing]);

  // Load buyer seats
  const fetchBuyerSeats = useCallback(
    (page: number, size: number, search: string, status: string | null) => {
      if (!id) return;
      if (status === 'All') status = '';
      buyerSeatService
        .getBuyerSeatsByOrganization(id, {
          page,
          size,
          search: search || undefined,
          status: status || undefined,
        })
        .then((res: BuyerSeatPageDto) => {
          setBuyerSeats(res.resultList);
          setBuyerSeatTotal(Number(res.totalItems));
        })
        .catch(() => {});
    },
    [id]
  );

  useEffect(() => {
    const timer = setTimeout(() => setBsSearchDebounced(bsSearch), 400);
    return () => clearTimeout(timer);
  }, [bsSearch]);

  useEffect(() => {
    fetchBuyerSeats(bsPage, bsRowsPerPage, bsSearchDebounced, bsStatus);
  }, [fetchBuyerSeats, bsPage, bsRowsPerPage, bsSearchDebounced, bsStatus]);

  // Load deals
  const fetchDeals = useCallback(
    (page: number, size: number, search: string, status: string | null) => {
      if (!id) return;
      if (status === 'ALL') status = '';
      applicationService
        .getDealsByOrganization(id, {
          page,
          size,
          search: search || undefined,
          status: status || undefined,
        })
        .then((res) => {
          setDeals(res.resultList);
          setDealsTotal(Number(res.totalItems));
        })
        .catch(() => {});
    },
    [id]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDSearchDebounced(dSearch), 400);
    return () => clearTimeout(timer);
  }, [dSearch]);

  useEffect(() => {
    fetchDeals(dPage, dRowsPerPage, dSearchDebounced, dStatus);
  }, [fetchDeals, dPage, dRowsPerPage, dSearchDebounced, dStatus]);

  // Edit mode helpers
  const enterEditMode = () => {
    if (!org || isEditing) return;
    selectedOwnerRef.current = org.ownerId
      ? { userId: org.ownerId, fullName: org.salesOwner ?? '', email: '' }
      : emptyOwner;
    setEditValues({
      name: org.name,
      email: org.email,
      phone: org.phone,
    });
    setIsEditing(true);
  };

  const handleFieldChange = (field: keyof OrganizationUpdateDto, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValues({});
    setIsDirty(false);
    selectedOwnerRef.current = emptyOwner;
  };

  const handleSave = async () => {
    if (!id || !org) return;
   
    try {
      const ownerSelection = selectedOwnerRef.current;
      const updatePayload: OrganizationUpdateDto = {
        ...editValues,
        ...(ownerSelection?.userId ? {
          salesOwner: ownerSelection.fullName,
          owner: ownerSelection.userId,
        } : {}),
      };
      const updated = await warrantService.updateOrganization(id, updatePayload);
      setOrg(updated);
      setIsEditing(false);
      setEditValues({});
      setIsDirty(false);
      setAlert({ open: true, severity: 'success', title: 'Saved', message: 'Organization updated.' });
    } catch (reason: any) {
      setAlert({ open: true, severity: 'error', title: 'Save failed', message: reason.message });
    } 
  };

  if (!org) return null;

  return (
    <PageWrapper>
      <OrganizationBreadcrumb
        items={[
          { label: 'Organizations', to: '/organization/list' },
          { label: org.name },
        ]}
      />

      <PageTitleRow>
        <TitleLeft>
          <OrgName>{org.name}</OrgName>
          <OrgIdLabel>Organization ID: {org.id}</OrgIdLabel>
        </TitleLeft>
        
          <TitleActions ref={actionsRef}>
            <Button variant="outlined" size="medium" onClick={handleCancel} >
              Cancel
            </Button>
            {isDirty && (
            <Button variant="contained" size="medium" onClick={handleSave} >
              Save
            </Button>
             )}
          </TitleActions>
       
      </PageTitleRow>

      {/* Card 1: Basic Info */}
      <Card
        ref={cardRef}
        onClick={enterEditMode}
        style={{ cursor: isEditing ? 'default' : 'pointer' }}
      >
        <CardTitle>Basic Information</CardTitle>
        <FieldsGrid>
          {/* Row 1 */}
          <FieldBlock>
            <FieldLabel>Organization ID</FieldLabel>
            <FieldValue>{org.id}</FieldValue>
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>
              Name
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <TextField
                size="small"
                value={editValues.name ?? ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                fullWidth
              />
            ) : (
              <FieldValue>{org.name}</FieldValue>
            )}
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Status</FieldLabel>
            <Chip
              label={STATUS_LABELS[org.status] ?? org.status}
              className={`table-chip ${
                org.status === ORG_STATUS.ACTIVE
                  ? 'organization-status-active'
                  : org.status === ORG_STATUS.INACTIVE
                  ? 'organization-status-inactive'
                  : 'organization-status-draft'
              }`}
              size="small"
            />
          </FieldBlock>

          {/* Row 2 */}
          <FieldBlock>
            <FieldLabel>
              Sales Owner
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <div onClick={(e) => e.stopPropagation()}>
                <OwnerSelectorField
                  initialOwner={selectedOwnerRef.current}
                  onOwnerChange={handleOwnerChange}
                  onDirty={() => setIsDirty(true)}
                />
              </div>
            ) : (
              <FieldValue>{org.salesOwner || '—'}</FieldValue>
            )}
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>
              Contact Email
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <TextField
                size="small"
                value={editValues.email ?? ''}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                fullWidth
              />
            ) : (
              <FieldValue>{org.email || '—'}</FieldValue>
            )}
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>
              Phone Number
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <TextField
                size="small"
                value={editValues.phone ?? ''}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                fullWidth
              />
            ) : (
              <FieldValue>{org.phone || '—'}</FieldValue>
            )}
          </FieldBlock>

          {/* Row 3 */}
          <FieldBlock>
            <FieldLabel>Created Date</FieldLabel>
            <FieldValue>{formatDate(org.createdAt)}</FieldValue>
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Total Buyer Seats</FieldLabel>
            <FieldValue>{org.buyerSeatsCount}</FieldValue>
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Deals</FieldLabel>
            <FieldValue>{org.dealsCount}</FieldValue>
          </FieldBlock>
        </FieldsGrid>
      </Card>

      {/* Card 2: Buyer Seats */}
      <Card>
        <CardHeaderRow>
          <CardTitle>Buyer Seats ({buyerSeatTotal})</CardTitle>
          <CardControls>
            <TextField
              select
              size="small"
              variant="outlined"
              value={bsStatus}
              onChange={(e) => { setBsStatus(e.target.value); setBsPage(0); }}
              SelectProps={{
                renderValue: (val) => {
                  const labels: Record<string, string> = {
                    All: 'All',
                    ACTIVE: 'Active',
                    INACTIVE: 'Inactive',
                  };
                  return labels[val as string] ?? String(val);
                },
              }}
              sx={{
                minWidth: 140,
                '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
              }}
            >
              {[
                { value: 'All', label: 'All' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
                { value: 'DISABLED', label: 'Disabled' },
              ].map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    backgroundColor: bsStatus === opt.value ? '#F5F5F5' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box component="span" sx={{ flexGrow: 1 }}>{opt.label}</Box>
                  {bsStatus === opt.value && (
                    <CheckIcon sx={{ color: 'primary.main', ml: 2 }} fontSize="small" />
                  )}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              placeholder="Search by Name or Buyer Seat ID"
              size="small"
              value={bsSearch}
              onChange={(e) => { setBsSearch(e.target.value); setBsPage(0); }}
              sx={{ minWidth: 330 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {bsSearch ? (
                      <IconButton size="small" onClick={() => { setBsSearch(''); setBsPage(0); }} sx={{ p: 0.5 }}>
                        <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                    ) : (
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </CardControls>
        </CardHeaderRow>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: '#F5F5F5', borderTop: '1px solid #0000001A' }}>
                <TableCell>Buyer Seat ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>SSP</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buyerSeats.length ? (
                buyerSeats.map((bs) => (
                  <TableRow key={bs.id}>
                    <TableCell>{bs.buyerSeatId}</TableCell>
                    <TableCell>{bs.name}</TableCell>
                    <TableCell>{bs.ssp || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={bs.status === 'ACTIVE' ? 'Active' : bs.status === 'DISABLED' ? 'Disabled' : 'Inactive'}
                        className={`table-chip ${bs.status === 'ACTIVE' ? 'organization-status-active' : 'organization-status-inactive'}`}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <NoMatches>No buyer seats found</NoMatches>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {buyerSeats.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    colSpan={4}
                    count={buyerSeatTotal}
                    rowsPerPage={bsRowsPerPage}
                    page={bsPage}
                    SelectProps={{ native: true }}
                    onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, newPage: number) => setBsPage(newPage)}
                    onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setBsRowsPerPage(parseInt(e.target.value, 10));
                      setBsPage(0);
                    }}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Card>

      {/* Card 3: Deals */}
      <Card>
        <CardHeaderRow>
          <CardTitle>Deals ({dealsTotal})</CardTitle>
          <CardControls>
            <TextField
              select
              size="small"
              variant="outlined"
              value={dStatus}
              onChange={(e) => { setDStatus(e.target.value); setDPage(0); }}
              SelectProps={{
                renderValue: (val) => {
                  const labels: Record<string, string> = {
                    ALL: 'All',
                    [DEAL_STATUS.ACTIVE]: 'Active',
                    [DEAL_STATUS.IN_CREATION]: 'In Creation',
                    [DEAL_STATUS.DRAFT]: 'Draft',
                    [DEAL_STATUS.ERROR]: 'Error',
                  };
                  return labels[val as string] ?? String(val);
                },
              }}
              sx={{
                minWidth: 140,
                '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
              }}
            >
              {[
                { value: 'ALL', label: 'All' },
                { value: DEAL_STATUS.ACTIVE, label: 'Active' },
                { value: DEAL_STATUS.IN_CREATION, label: 'In Creation' },
                { value: DEAL_STATUS.DRAFT, label: 'Draft' },
                { value: DEAL_STATUS.ERROR, label: 'Error' },
              ].map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    backgroundColor: dStatus === opt.value ? '#F5F5F5' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box component="span" sx={{ flexGrow: 1 }}>{opt.label}</Box>
                  {dStatus === opt.value && (
                    <CheckIcon sx={{ color: 'primary.main', ml: 2 }} fontSize="small" />
                  )}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              placeholder="Search by Name or Deal ID"
              size="small"
              value={dSearch}
              onChange={(e) => { setDSearch(e.target.value); setDPage(0); }}
              sx={{ minWidth: 330 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {dSearch ? (
                      <IconButton size="small" onClick={() => { setDSearch(''); setDPage(0); }} sx={{ p: 0.5 }}>
                        <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                    ) : (
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </CardControls>
        </CardHeaderRow>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: '#F5F5F5', borderTop: '1px solid #0000001A' }}>
                <TableCell>Deal ID</TableCell>
                <TableCell>Deal Name</TableCell>
                <TableCell>Buyer Seat</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.length ? (
                deals.map((deal) => (
                  <TableRow key={deal.id}>
                    <TableCell>{deal.xandrDealId}</TableCell>
                    <TableCell>{deal.name}</TableCell>
                    <TableCell>
                      {deal.buyerSeatName ? `${deal.buyerSeatName} (${deal.buyerSeatId})` : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          deal.status === DEAL_STATUS.ACTIVE
                            ? 'Active'
                            : deal.status === DEAL_STATUS.DRAFT
                            ? 'Draft'
                            : deal.status === DEAL_STATUS.IN_CREATION
                            ? 'In creation'
                            : deal.status === DEAL_STATUS.ERROR
                            ? 'Error'
                            : deal.status
                        }
                        className={`table-chip ${
                          deal.status === DEAL_STATUS.ACTIVE
                            ? 'deal-status-active'
                            : deal.status === DEAL_STATUS.DRAFT
                            ? 'deal-status-draft'
                            : deal.status === DEAL_STATUS.IN_CREATION
                            ? 'deal-status-in-creation'
                            : deal.status === DEAL_STATUS.ERROR
                            ? 'deal-status-error'
                            : ''
                        }`}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <NoMatches>No deals found</NoMatches>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {deals.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    colSpan={4}
                    count={dealsTotal}
                    rowsPerPage={dRowsPerPage}
                    page={dPage}
                    SelectProps={{ native: true }}
                    onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, newPage: number) => setDPage(newPage)}
                    onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDRowsPerPage(parseInt(e.target.value, 10));
                      setDPage(0);
                    }}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Card>
    </PageWrapper>
  );
};

export default OrganizationDetails;

// ─── Styled components ───────────────────────────────────────────────────────

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PageTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TitleLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OrgName = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  color: ${getColor('text')};
`;

const OrgIdLabel = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
`;

const TitleActions = styled.div`
  display: flex;
  gap: 8px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #0000001a;
  border-radius: 8px;
  padding: 20px 24px;
`;

const CardTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 500;
  color: ${getColor('text')};
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 32px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 2px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const FieldValue = styled.span`
  font-size: 14px;
  color: ${getColor('text')};
`;

const NoMatches = styled.div`
  font-size: 14px;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px;
  color: rgba(0, 0, 0, 0.4);
`;
